import React, { memo } from "react";
import { List } from "react-window";
import { ReactComponent as PlusIcon } from "../icons/plus.svg";
import { ReactComponent as MinusIcon } from "../icons/minus.svg";

const InputCell = memo(
  ({ value, columnId, rowIndex, rowId, onUpdate, tablePermission }) => {
    const [localValue, setLocalValue] = React.useState(value);
    const inputRef = React.useRef(null);

    // Sync external value if it changes from outside
    React.useEffect(() => {
      setLocalValue(value);
    }, [value]);

    const handleChange = (e) => {
      setLocalValue(e.target.value);
    };

    const handleBlur = () => {
      if (tablePermission !== "read-only") {
        onUpdate(localValue, columnId, rowIndex, rowId);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Enter" && tablePermission !== "read-only") {
        onUpdate(localValue, columnId, rowIndex, rowId);
        // Move focus to the next cell if possible
        const form = inputRef.current.closest(".table-row");
        if (form) {
          const inputs = Array.from(form.querySelectorAll("input"));
          const currentIndex = inputs.indexOf(inputRef.current);
          if (currentIndex + 1 < inputs.length) {
            inputs[currentIndex + 1].focus();
          }
        }
      }
    };

    return (
      <input
        ref={inputRef}
        style={{
        width: "100%",
        border: "none",
        borderRadius: 0,
        backgroundColor: "transparent",
        textAlign: "left",
        color: "black",
        fontFamily: "'Roboto Flex', sans-serif",
        fontSize: 14,
        fontWeight: 400,
        lineHeight: "40px",
        padding: "0 4px",
        margin: "0",
        boxSizing: "border-box",
        transition: "background-color 0.2s",
        outline: "none",
          border: "1px solid #ccc",
      }}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={tablePermission === "read-only"}
      />
    );
  }
);

// New component for header title input
const HeaderTitleCell = memo(({ value, columnId, onUpdate, tablePermission }) => {
  const [localValue, setLocalValue] = React.useState(value);

  const handleChange = (e) => {
    setLocalValue(e.target.value);
  };

  const handleBlur = () => {
    if (tablePermission !== "read-only") {
      onUpdate(localValue, columnId);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && tablePermission !== "read-only") {
      onUpdate(localValue, columnId);
    }
  };

  return (
    <input
      style={{
        width: "100%",
        border: "none",
        backgroundColor: "transparent",
        textAlign: "center",
        color: "#233A80",
        fontFamily: "'Roboto Flex', sans-serif",
        fontSize: 12,
        fontWeight: 700,
        lineHeight: "40px",
        padding: "0 4px",
        boxSizing: "border-box",
        transition: "background-color 0.2s",
        outline: "none",
      }}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      disabled={tablePermission === "read-only"}
    />
  );
});

// Header row component
const HeaderRow = ({
  columns,
  tablePermission,
  onUpdateColumnTitle,
  onAddColumn,
  onDeleteColumn,
  onAddRow, // New prop for adding a row at the top
  indexWidth
}) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: "4px 8px",
        borderBottom: "2px solid #ddd",
        backgroundColor: "#e8e8e8",
        fontWeight: "bold",
      }}
      className="table-header-row"
    >
      {/* Actions header with plus icon for adding new row at top */}
      <div
  style={{
    position: "relative", // ✅ parent must be relative for absolute children
    width: "60px",
    height: "60px",
  }}
>
  {/* Add Column (+) → top right */}
  <PlusIcon
    onClick={() => tablePermission !== "read-only" && onAddColumn(null)}
    style={{
      position: "absolute",
      top: "0px",
      right: "0px",
      cursor: tablePermission !== "read-only" ? "pointer" : "not-allowed",
      width: "16px",
      height: "16px",
      opacity: tablePermission === "read-only" ? 0.5 : 1,
    }}
    title="Adicionar nova coluna"
  />

  {/* Add Row (+) → bottom left */}
  <PlusIcon
    onClick={() => tablePermission !== "read-only" && onAddRow(0)}
    style={{
      position: "absolute",
      bottom: "0px",
      left: "0px",
      cursor: tablePermission !== "read-only" ? "pointer" : "not-allowed",
      width: "16px",
      height: "16px",
      opacity: tablePermission === "read-only" ? 0.5 : 1,
    }}
    title="Adicionar nova linha no topo"
  />
</div>

      {/* Row Index Header */}
      <div
        style={{
    width: `${indexWidth}px`,   // ✅ dynamic width
    textAlign: "center",
    marginRight: "8px",
    color: "#555",
    flexShrink: 0,
  }}
      >
        #
      </div>

      {/* Column Headers */}
{columns.map((column) => (
  <div
    key={column.column_id}
    style={{
      display: "flex",
      flexDirection: "column", // ✅ vertical stacking
      alignItems: "center",
      flex: 1,
      marginRight: "4px",
    }}
  >
    {/* Actions above the header title */}
    <div style={{ display: "flex", marginBottom: "2px" }}>
      <PlusIcon
        onClick={() => tablePermission !== "read-only" && onAddColumn(column.column_id)}
        style={{
          cursor: tablePermission !== "read-only" ? "pointer" : "not-allowed",
          width: "16px",
          height: "16px",
          marginRight: "2px",
          opacity: tablePermission === "read-only" ? 0.5 : 1,
        }}
        title="Adicionar nova coluna após esta"
      />
      <MinusIcon
        onClick={() => tablePermission !== "read-only" && onDeleteColumn(column.column_id)}
        style={{
          cursor: tablePermission !== "read-only" ? "pointer" : "not-allowed",
          width: "16px",
          height: "16px",
          opacity: tablePermission === "read-only" ? 0.5 : 1,
        }}
        title="Apagar esta coluna"
      />
    </div>

    {/* Header title */}
    <HeaderTitleCell
      value={column.column_name}
      columnId={column.column_id}
      onUpdate={onUpdateColumnTitle}
      tablePermission={tablePermission}
    />
  </div>
))}
    </div>
  );
};

const Row = ({
  index,
  style,
  rowIds,
  columns,
  tableColumnData,
  tablePermission,
  addNewRow,
  onDeleteRow,
  handleDataChange,
  indexWidth
}) => {
  const rowId = rowIds[index];

  const getColumnData = (columnId) => tableColumnData[columnId]?.data || [];

  return (
    <div
      style={{
        ...style,
        display: "flex",
        alignItems: "center",
        padding: "0",
        borderBottom: "1px solid #eee",
        backgroundColor: index % 2 === 0 ? "#fafafa" : "#fff",
        transition: "background-color 0.2s",
      }}

    >
      {/* Actions */}
      <div style={{ display: "flex", alignItems: "center", marginRight: "8px", width: "60px" }}>
        <PlusIcon
          onClick={() => tablePermission !== "read-only" && addNewRow(rowId)}
          style={{
            cursor: tablePermission !== "read-only" ? "pointer" : "not-allowed",
            marginRight: "4px",
            opacity: tablePermission === "read-only" ? 0.5 : 1,
          }}
          title="Adicionar nova linha após esta"
        />
        <MinusIcon
          onClick={() => tablePermission !== "read-only" && onDeleteRow(rowId, index)}
          style={{
            cursor: tablePermission !== "read-only" ? "pointer" : "not-allowed",
            opacity: tablePermission === "read-only" ? 0.5 : 1,
          }}
          title="Apagar esta linha"
        />
      </div>

      {/* Row Index */}
      <div
        style={{
    width: `${indexWidth}px`,
    textAlign: "center",
    marginRight: "8px",
    color: "#555",
    flexShrink: 0,
  }}
      >
        {index + 1}
      </div>

      {/* Data Cells */}
      {columns.map((column) => (
        <div key={column.column_id} style={{ flex: 1}}>
          <InputCell
            value={getColumnData(column.column_id)[index] || ""}
            columnId={column.column_id}
            rowIndex={index}
            rowId={rowId}
            onUpdate={handleDataChange}
            tablePermission={tablePermission}
          />
        </div>
      ))}
    </div>
  );
};

const TableBody = ({
  rowIds,
  columns,
  tableColumnData,
  tablePermission,
  addNewRow,
  onDeleteRow,
  handleDataChange,
  onUpdateColumnTitle,
  onAddColumn,
  onDeleteColumn,
}) => {
  // Calculate total width based on number of columns
  const columnWidth = 180; // Fixed width per column
  const actionsWidth = 60; // Width for actions column

  const gapWidth = 12; // Total gap (marginRight 8px + some padding)

  const maxDigits = String(rowIds.length).length;
  const indexWidth = Math.max(30, maxDigits * 10);

  const totalWidth = actionsWidth + indexWidth + (columns.length * (columnWidth + 4)) + gapWidth;

  return (
    <div style=
    {{ minWidth: totalWidth,
      height: "100%",
      border: "1px solid #ddd",
      borderRadius: "6px",
      boxSizing: "border-box",
      overflowX: "auto", // Enable both horizontal and vertical scrolling
      display: "flex",
        flexDirection: "column"
    }}>
      <div style={{
        position: "sticky",
        top: 0,
        zIndex: 1,
        backgroundColor: "#e8e8e8",
        borderBottom: "2px solid #ddd"
      }}>
        <HeaderRow
          columns={columns}
          tablePermission={tablePermission}
          onUpdateColumnTitle={onUpdateColumnTitle}
          onAddColumn={onAddColumn}
          onDeleteColumn={onDeleteColumn}
          onAddRow={addNewRow}
          totalWidth={totalWidth}
          indexWidth={indexWidth}
        />
      </div>

  <List
    rowCount={rowIds.length}
    rowHeight={40}
    rowComponent={Row}
    rowProps={{
    rowIds,
    columns,
    tableColumnData,
    tablePermission,
    addNewRow,
    onDeleteRow,
    handleDataChange,
    indexWidth
  }}
/>
      </div>
  );
};

export default TableBody;
